import type { ReactNode } from 'react';

import { REPLY_DRAFT_COPY } from '../copy';

import { Button, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { getAccessibilityProps } from '@/util/accessibilityProps';

type DraftFooterProps = {
  /** Goes to the previous step. Left out on the first step, which has nowhere to go back to. */
  onBack?: () => void;
  /** Drawn above the buttons, such as what is still to fix or why the last attempt failed. */
  notice?: ReactNode;
  /** The step's one forward action. */
  children: ReactNode;
};

/**
 * The foot of every step: Back on the left and the step's way forward filling
 * the rest, so moving on is always in the same place and under the thumb.
 * Back is outlined and the way forward is the one filled button, so the two
 * are told apart by weight as well as position. A notice sits directly above,
 * so the reason a step is held is read before the button is reached.
 */
export function DraftFooter({ onBack, notice, children }: DraftFooterProps) {
  return (
    <VStack className="gap-2 px-4 pb-4 pt-8">
      {notice}

      <HStack className="gap-3">
        {onBack ? (
          <Button
            size="xl"
            variant="outline"
            action="secondary"
            className="min-h-12"
            onPress={onBack}
            testID="reply-draft-back"
            {...getAccessibilityProps({
              role: 'button',
              label: REPLY_DRAFT_COPY.back,
              hint: REPLY_DRAFT_COPY.backHint,
            })}>
            <ButtonText className="text-secondary-700">{REPLY_DRAFT_COPY.back}</ButtonText>
          </Button>
        ) : null}

        <View className="flex-1">{children}</View>
      </HStack>
    </VStack>
  );
}

/** The filled button a step moves forward with. */
export function DraftFooterButton({
  label,
  hint,
  onPress,
  testID,
}: {
  label: string;
  hint: string;
  onPress: () => void;
  testID: string;
}) {
  return (
    <Button
      size="xl"
      action="primary"
      className="min-h-12 w-full"
      onPress={onPress}
      testID={testID}
      {...getAccessibilityProps({ role: 'button', label, hint })}>
      <ButtonText>{label}</ButtonText>
    </Button>
  );
}
